import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash-es';
import ReactDOM from 'react-dom';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components';
import { getRandomId } from 'utils';

import { ILocation } from '@rbi-ctg/frontend';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import useMediaQuery from 'hooks/use-media-query';
import { loadRegion } from 'state/intl/load-region';
import { isValidPosition } from 'utils/geolocation';
import { cornersForPoint, hypotenuse } from 'utils/geometry';
import DEFAULT_MAP_ZOOM from 'utils/restaurant/default-map-zoom';

import mapDefaults from './defaults';
import {
  addListener,
  clearListenerCache,
  createDestinationMarker,
  createDriverLocationMarker,
  createStoreLocationMarker,
  createUserLocationMarker,
} from './map-api-utils';

const MapContainer = styled.div`
  background: ${Styles.color.white};
  flex: 1 1 auto;
  height: 100%;
  position: relative;
  width: 100%;
`;

export enum MarkerTypes {
  User = 'user',
  Store = 'store',
  Destination = 'destination',
  Driver = 'driver',
}

interface IDeriveMarker {
  type: MarkerTypes;
  location: ILocation;
  onClick(): void;
  iconOverrideUrl?: string;
}

type Marker = {
  marker: google.maps.Marker;
  inactive(): void;
  active(): void;
};

const computeHeight = (innerHeight: number, isMobile: boolean) =>
  isMobile ? innerHeight - 56 - 50 : innerHeight - 78;

type MapEventListener = (...args: any[]) => void;
export interface IUseMap {
  disableControls?: boolean;
  eventListeners?: {
    [key: string]: MapEventListener | Array<MapEventListener>;
  };
  position?: ILocation;
}

export default function useMap({
  eventListeners = {},
  position = {} as ILocation,
  ...props
}: IUseMap = {}) {
  const domRef = useRef(null);
  const region = loadRegion() || false;
  const staggerIndex = useRef(0);
  const defaultPosition = region && mapDefaults[region] ? mapDefaults[region] : mapDefaults.default;
  // track when the map container has been loaded into the dom
  const [mapContainerLoadedInDom, setMapContainerLoadedInDom] = useState(false);
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const startPosition = position.lat
    ? {
        lat: position.lat,
        lng: position.lng,
      }
    : defaultPosition;
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef({});
  const [zoom, setZoom] = useState(
    startPosition !== defaultPosition ? defaultPosition.zoom : DEFAULT_MAP_ZOOM
  );
  const { width: innerWidth, height: innerHeight } = useWindowDimensions();
  const isMobile = useMediaQuery('mobile');
  /**
   * these are only computed once to ensure any
   * groq queries sent out have reasonable defaults
   * since map may not be drawn by the time first query fires
   */
  /* eslint-disable react-hooks/exhaustive-deps */
  const height = useMemo(() => computeHeight(innerHeight, isMobile), []);
  const diagonal = useMemo(() => hypotenuse(innerWidth, height), []);
  const { ne, sw } = useMemo(
    () => cornersForPoint(startPosition.lat, startPosition.lng, zoom, diagonal),
    []
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const [neLat, setNeLat] = useState(ne.lat);
  const [neLng, setNeLng] = useState(ne.lng);
  const [swLat, setSwLat] = useState(sw.lat);
  const [swLng, setSwLng] = useState(sw.lng);
  const [center, setCenter] = useState(startPosition);

  /*
   * SETUP MAP
   *
   * This loads the map api internally, sets up some options
   * and begins the whole process.
   *
   * NOTES:
   * - This needs to be an effect
   * - It has required listener clean ups
   * - Can not re-render map created from this hook, will error.
   *
   */

  const prepareMap = useCallback(
    mapId => {
      const options = {
        center: startPosition,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
        disableDefaultUI: true,
        zoom,
        gestureHandling: props.disableControls ? 'none' : 'greedy',
      } as google.maps.MapOptions;

      const mapInstance = new window.google.maps.Map(domRef.current, options);

      const setClampedMapBounds = () => {
        const mapBounds = mapInstance.getBounds() as google.maps.LatLngBounds;
        const mapCenter = mapInstance.getCenter() as google.maps.LatLng;

        const northEast = mapBounds.getNorthEast();
        const southWest = mapBounds.getSouthWest();

        ReactDOM.unstable_batchedUpdates(() => {
          setNeLat(northEast.lat());
          setNeLng(northEast.lng());
          setSwLat(southWest.lat());
          setSwLng(southWest.lng());
          setCenter({
            lat: mapCenter.lat(),
            lng: mapCenter.lng(),
          });
        });
      };

      window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const mapZoom = mapInstance.getZoom();
        setZoom(mapZoom);
        setClampedMapBounds();
      });

      /**
       *  Called by dragstart and zoom_changed listeners to prevent keyboard remaining open on android devices
       *  when dragging/zooming map while search field has focus
       */
      const blurActiveElement = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur?.();
      };

      addListener('dragstart', mapInstance, blurActiveElement, mapId);

      addListener('bounds_changed', mapInstance, debounce(setClampedMapBounds, 500), mapId);

      addListener(
        'zoom_changed',
        mapInstance,
        debounce(() => {
          blurActiveElement();
          const mapZoom = mapInstance.getZoom();
          setZoom(mapZoom);
        }, 500),
        mapId
      );

      Object.keys(eventListeners).forEach(eventName => {
        const handlers = eventListeners[eventName];
        if (typeof handlers === 'function') {
          addListener(eventName, mapInstance, handlers, mapId);
        }
        if (Array.isArray(handlers)) {
          handlers.forEach(handler => {
            addListener(eventName, mapInstance, handler, mapId);
          });
        }
      });
      // Set a minZoom option to prevent the user to zoom out too much
      mapInstance.setOptions({ minZoom: 5 });
      setMap(mapInstance);
    },
    [eventListeners, props.disableControls, startPosition, zoom]
  );

  useEffect(() => {
    const shouldLoad = !!domRef.current;

    if (!shouldLoad) {
      return;
    }

    const mapId = getRandomId();

    prepareMap(mapId);

    return () => clearListenerCache(mapId);
    /*
     * NOTE:
     * - Only run when we know the map container has been loaded in the dom ~ mapContainerLoadedInDom
     * - mapContainerLoadedInDom starts as false and is only set to true when we have a dom ref
     * - And the domRef.current will be set
     */
  }, [mapContainerLoadedInDom]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadedIntoDom = useCallback(ref => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setMapContainerLoadedInDom(true);
    }
  }, []);

  const panTo = useCallback(
    newPosition => {
      if (!map) {
        return;
      }

      if (isValidPosition(newPosition)) {
        map.panTo(newPosition);
      }

      if (map.getZoom() < DEFAULT_MAP_ZOOM && newPosition !== defaultPosition) {
        map.setZoom(DEFAULT_MAP_ZOOM);
      }
    },
    [defaultPosition, map]
  );

  useEffect(() => {
    if (map && isValidPosition(position)) {
      panTo(position);
    }
    // We recreate a new object above each render, which makes a simple object
    // compare fail. We only want to run this effect when the actual values change
  }, [map, position.lat, position.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Returns marker of proper type.
   */
  const deriveMarker = useCallback(
    ({ type, location, onClick, iconOverrideUrl }: IDeriveMarker): Marker | void => {
      if (isValidPosition(location)) {
        switch (type) {
          case MarkerTypes.User:
            return createUserLocationMarker(location, map);
          case MarkerTypes.Store:
            return createStoreLocationMarker(location, map, onClick);
          case MarkerTypes.Driver:
            return createDriverLocationMarker(location, map, iconOverrideUrl);
          case MarkerTypes.Destination:
            return createDestinationMarker(location, map);

          default:
            throw new Error(`Unsupported type: ${type}`);
        }
      }
    },
    [map]
  );

  /*
   * Creates a marker for a given type and location. If marker
   * already placed on map, updates accordingly.
   */
  const createMarker = useCallback(
    ({ type, location, onClick, introAnim = false, iconOverrideUrl }) => {
      const isSingleUseMarker = [MarkerTypes.User, MarkerTypes.Driver].includes(type);

      if (!libraryLoaded || !map) {
        return null;
      }

      if (!isValidPosition(location)) {
        return null; // bail if location is not known
      }

      // This updates the marker if it has already been added to map.
      const key = isSingleUseMarker ? type : `${location.lat}${location.lng}`;

      if (markers.current[key]) {
        markers.current[key].marker.setMap(map);
        window.google.maps.event.clearInstanceListeners(markers.current[key].marker);
        window.google.maps.event.addListener(markers.current[key].marker, 'click', onClick);

        if (isSingleUseMarker && location.lat && location.lng) {
          markers.current[key].marker.setPosition(location);
        }

        return markers.current[key];
      }

      const marker = deriveMarker({ type, location, onClick, iconOverrideUrl }) as Marker;
      markers.current[key] = marker;

      // This animates the markers in if introAnim set to true
      if (type === MarkerTypes.Store && introAnim) {
        marker.marker.setOpacity(0);
        staggerIndex.current += 1;
        setTimeout(() => {
          marker.marker.setAnimation(window.google.maps.Animation.DROP);
          marker.marker.setOpacity(1);
          staggerIndex.current = Math.max(staggerIndex.current - 1, 0);
        }, 60 * staggerIndex.current);
      }

      return marker;
    },
    [deriveMarker, libraryLoaded, map]
  );

  const fitAndCenterFromCoords = useCallback(
    debounce((coordsArray: ILocation[]) => {
      if (!libraryLoaded || !map) {
        return;
      }

      if (coordsArray.length >= 2) {
        const markerBounds = coordsArray.reduce((bounds, coord) => {
          bounds.extend(coord);
          return bounds;
        }, new window.google.maps.LatLngBounds());

        const marker1 = new window.google.maps.LatLng({
          lat: markerBounds.getSouthWest().lat(),
          lng: markerBounds.getSouthWest().lng(),
        });
        const marker2 = new window.google.maps.LatLng({
          lat: markerBounds.getNorthEast().lat(),
          lng: markerBounds.getNorthEast().lng(),
        });

        if (!marker1.equals(marker2)) {
          // Adding a 100px padding around the bounds to cover for
          // icons at the edge
          map.fitBounds(markerBounds, 100);
        }
      }
    }, 500),
    [map, libraryLoaded]
  );

  return {
    createMarker,
    zoom,
    neLat,
    neLng,
    swLat,
    swLng,
    center,
    markers,
    domRef,
    map: React.createElement(MapContainer, { ref: onLoadedIntoDom }),
    mapInstance: map,
    MarkerTypes,
    panTo,
    fitAndCenterFromCoords,
  };
}
